import * as i0 from '@angular/core';
import { Injectable, Pipe, NgModule, createComponent, Inject, Component, ViewEncapsulation, InjectionToken } from '@angular/core';
import * as i7 from '@angular/common';
import { CommonModule, DOCUMENT } from '@angular/common';
import * as i1 from '@angular/forms';
import { UntypedFormControl, Validators, UntypedFormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';

const localStorageConfigKey = 'localConfigs';
class ConfigLocalStorageService {
    hasSettings() {
        const localAppSettings = localStorage?.getItem(localStorageConfigKey);
        return localAppSettings !== null;
    }
    getSettings() {
        return JSON.parse(localStorage.getItem(localStorageConfigKey) || '{}');
    }
    getDefaultKey() {
        return this.getSettings().defaultKey;
    }
    hasKey(key) {
        if (this.localAppSettings?.configs) {
            const hasKey = key in this.localAppSettings.configs;
            return hasKey;
        }
        return false;
    }
    hasConfigs() {
        return this.localAppConfigKeys?.length > 1;
    }
    getKey(key) {
        if (this.hasSettings()) {
            try {
                return this.localAppSettings.configs[key];
            }
            catch (error) {
                console.error('ConfigLocalStorageService', 'getKey', { error });
            }
        }
        return null;
    }
    read() {
        if (localStorage) {
            this.localAppSettings = JSON.parse(localStorage.getItem(localStorageConfigKey) || '{}');
            this.localAppConfigKeys = Object.keys(this.localAppSettings.configs || {});
        }
    }
    addConfig(key, value) {
        if (localStorage) {
            const localConfigs = JSON.parse(localStorage.getItem(localStorageConfigKey) || '{}');
            localConfigs.configs = localConfigs.configs || {};
            localConfigs.configs[key] = value;
            localStorage.setItem(localStorageConfigKey, JSON.stringify(localConfigs));
            this.read();
        }
    }
    clear() {
        if (localStorage) {
            localStorage.removeItem(localStorageConfigKey);
        }
        this.read();
    }
    selectConfig(key) {
        if (localStorage) {
            const localConfigs = JSON.parse(localStorage.getItem(localStorageConfigKey) || '{}');
            localConfigs.defaultKey = key;
            localStorage.setItem(localStorageConfigKey, JSON.stringify(localConfigs));
            this.read();
        }
    }
}
ConfigLocalStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: ConfigLocalStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ConfigLocalStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: ConfigLocalStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: ConfigLocalStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class JsonToSchemaPipe {
    static isString(str) {
        if (typeof str?.valueOf() === 'string') {
            return true;
        }
        return false;
    }
    static isBoolean(str) {
        if (typeof str?.valueOf() === 'boolean') {
            return true;
        }
        return false;
    }
    transform(source, data) {
        Object.keys(data).forEach(key => {
            if (JsonToSchemaPipe.isString(data[key])) {
                source[key] = {
                    type: 'string'
                };
            }
            else if (JsonToSchemaPipe.isBoolean(data[key])) {
                source[key] = {
                    type: 'boolean'
                };
            }
            else {
                source[key] = {
                    type: 'object'
                };
                this.transform(source[key], data[key]);
            }
        });
        return source;
    }
}
JsonToSchemaPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
JsonToSchemaPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipe, name: "jsonToSchema" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'jsonToSchema'
                }]
        }] });
class JsonToSchemaPipeModule {
}
JsonToSchemaPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
JsonToSchemaPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipeModule, declarations: [JsonToSchemaPipe], imports: [CommonModule], exports: [JsonToSchemaPipe] });
JsonToSchemaPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: JsonToSchemaPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        JsonToSchemaPipe
                    ],
                    exports: [
                        JsonToSchemaPipe
                    ]
                }]
        }] });

class SchemaToFormLayoutPipe {
    transform(layout, schema) {
        Object.keys(schema).forEach(key => {
            if (key !== 'type') {
                if (schema[key].type !== 'object') {
                    layout.push({ name: key, type: schema[key].type });
                }
                else {
                    const childLayout = { name: key, controls: [], type: schema[key].type };
                    layout.push(childLayout);
                    this.transform(childLayout.controls, schema[key]);
                }
            }
        });
        return layout;
    }
}
SchemaToFormLayoutPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SchemaToFormLayoutPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipe, name: "schemaToFormLayout" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'schemaToFormLayout'
                }]
        }] });
class SchemaToFormLayoutPipeModule {
}
SchemaToFormLayoutPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SchemaToFormLayoutPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipeModule, declarations: [SchemaToFormLayoutPipe], imports: [CommonModule], exports: [SchemaToFormLayoutPipe] });
SchemaToFormLayoutPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormLayoutPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        SchemaToFormLayoutPipe
                    ],
                    exports: [
                        SchemaToFormLayoutPipe
                    ]
                }]
        }] });

class SchemaToFormGroupPipe {
    transform(formGroup, schema, readOnly = false) {
        Object.keys(schema).forEach(key => {
            if (key !== 'key') {
                if (schema[key]?.type === 'string') {
                    formGroup.addControl(key, new UntypedFormControl({ value: '', disabled: readOnly }, [Validators.required]));
                }
                else if (schema[key]?.type === 'boolean') {
                    formGroup.addControl(key, new UntypedFormControl({ value: '', disabled: readOnly }, [Validators.required]));
                }
                else if (schema[key]?.type === 'object') {
                    const childFormGroup = new UntypedFormGroup({}, [Validators.required]);
                    formGroup.addControl(key, childFormGroup);
                    this.transform(childFormGroup, schema[key], readOnly);
                }
            }
        });
        return formGroup;
    }
}
SchemaToFormGroupPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SchemaToFormGroupPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipe, name: "schemaToFormGroup" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'schemaToFormGroup'
                }]
        }] });
class SchemaToFormGroupPipeModule {
}
SchemaToFormGroupPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SchemaToFormGroupPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipeModule, declarations: [SchemaToFormGroupPipe], imports: [CommonModule], exports: [SchemaToFormGroupPipe] });
SchemaToFormGroupPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SchemaToFormGroupPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        SchemaToFormGroupPipe
                    ],
                    exports: [
                        SchemaToFormGroupPipe
                    ]
                }]
        }] });

class AppConfigService {
    constructor(document, appRef, injector) {
        this.document = document;
        this.appRef = appRef;
        this.injector = injector;
    }
    static getAppConfigManagerComponent() {
        return Promise.resolve().then(function () { return switcher_component; }).then((it) => it.SwitcherComponent);
    }
    showAppConfigSwitcher() {
        if (!this.componentHost) {
            AppConfigService.getAppConfigManagerComponent().then((componentClass) => {
                this.layer = this.document.createElement('div');
                this.document.body.appendChild(this.layer);
                this.layer.style.position = 'fixed';
                this.layer.style.inset = '0';
                this.layer.style.display = 'flex';
                this.layer.style.justifyContent = 'center';
                this.layer.style.alignItems = 'center';
                this.layer.classList.add('b-layer-app-config');
                this.componentHost = this.document.createElement('div');
                this.layer.appendChild(this.componentHost);
                const componentRef = createComponent(componentClass, {
                    environmentInjector: this.appRef.injector,
                    elementInjector: this.injector,
                    hostElement: this.componentHost
                });
                // Attach incoming component to the appRef so that it's inside the ng component tree
                this.hostView = componentRef.hostView;
                if (this.hostView) {
                    this.appRef.attachView(this.hostView);
                }
                componentRef.changeDetectorRef.detectChanges();
            }, (error) => {
                console.log('[Error] AppConfigService showAppConfigSettings', { error });
            });
        }
    }
    quit() {
        if (this.hostView) {
            this.appRef.detachView(this.hostView);
        }
        if (this.layer) {
            this.document.body.removeChild(this.layer);
        }
        this.hostView = undefined;
        this.layer = undefined;
        this.componentHost = undefined;
    }
}
AppConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: AppConfigService, deps: [{ token: DOCUMENT }, { token: i0.ApplicationRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
AppConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: AppConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: AppConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ApplicationRef }, { type: i0.Injector }]; } });

const defaultConfigKey = 'default';
class SwitcherComponent {
    constructor(baseAppConfig, fb, appConfigService, configLocalStorageService, jsonToSchemaPipe, schemaToFormLayoutPipe, schemaToFormGroupPipe) {
        this.baseAppConfig = baseAppConfig;
        this.fb = fb;
        this.appConfigService = appConfigService;
        this.configLocalStorageService = configLocalStorageService;
        this.jsonToSchemaPipe = jsonToSchemaPipe;
        this.schemaToFormLayoutPipe = schemaToFormLayoutPipe;
        this.schemaToFormGroupPipe = schemaToFormGroupPipe;
        const { isDefault, ...appConfig } = this.baseAppConfig;
        const schema = this.jsonToSchemaPipe.transform({ type: 'object' }, appConfig);
        const configKey = { type: 'string' };
        const configForm = this.fb.group({});
        this.schemaToFormGroupPipe.transform(configForm, { ...schema, configKey });
        this.configForm = configForm;
        const readOnlyConfigForm = this.fb.group({});
        this.schemaToFormGroupPipe.transform(readOnlyConfigForm, schema, true);
        this.readOnlyConfigForm = readOnlyConfigForm;
        this.formLayout = this.schemaToFormLayoutPipe.transform([], { ...schema, configKey });
        this.readOnlyFormLayout = this.schemaToFormLayoutPipe.transform([], schema);
        this.configLocalStorageService.read();
        const defaultKey = this.configLocalStorageService.getDefaultKey() || defaultConfigKey;
        if (defaultKey === defaultConfigKey) {
            this.configLocalStorageService.addConfig(defaultConfigKey, appConfig);
            this.configLocalStorageService.selectConfig(defaultKey);
        }
        this.selectedKey = defaultKey;
        if (defaultKey && this.configLocalStorageService.hasKey(defaultKey)) {
            try {
                this.readOnlyConfigForm.setValue(this.configLocalStorageService.getKey(defaultKey));
            }
            catch (error) {
                console.error('BuilderComponent', 'constructor', `error ${error}`);
            }
        }
        else {
            this.readOnlyConfigForm.setValue(appConfig);
        }
    }
    get selectedKey() {
        return this._selectedKey;
    }
    set selectedKey(value) {
        this._selectedKey = value;
        this.validateSelectedKey();
    }
    onSubmit(event) {
        const isValid = this.configForm.valid;
        const { configKey, ...formValue } = this.configForm.value;
        if (isValid) {
            this.writeToLocal(configKey, formValue);
            this.configForm.reset();
        }
        else {
            console.error('BuilderComponent', 'onSubmit form is invalid', this.configForm.value);
        }
    }
    writeToLocal(key, value) {
        this.configLocalStorageService.addConfig(key, value);
        this.selectedKey = key;
    }
    clearConfigs() {
        this.configLocalStorageService.clear();
        this.selectedKey = defaultConfigKey;
    }
    validateSelectedKey() {
        const { isDefault, ...appConfig } = this.baseAppConfig;
        const hasKeyInLocalConfig = this.configLocalStorageService.hasKey(this.selectedKey);
        if (hasKeyInLocalConfig) {
            const selectedAppConfig = this.configLocalStorageService.getKey(this.selectedKey);
            if (selectedAppConfig) {
                this.readOnlyConfigForm.setValue(selectedAppConfig);
            }
        }
        else {
            this.readOnlyConfigForm.setValue(appConfig);
        }
    }
    selectConfig() {
        this.configLocalStorageService.selectConfig(this.selectedKey);
    }
    quit() {
        this.appConfigService.quit();
    }
}
SwitcherComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SwitcherComponent, deps: [{ token: APP_CONFIG }, { token: i1.UntypedFormBuilder }, { token: AppConfigService }, { token: ConfigLocalStorageService }, { token: JsonToSchemaPipe }, { token: SchemaToFormLayoutPipe }, { token: SchemaToFormGroupPipe }], target: i0.ɵɵFactoryTarget.Component });
SwitcherComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.8", type: SwitcherComponent, isStandalone: true, selector: "mil-config-switcher", providers: [
        JsonToSchemaPipe,
        SchemaToFormLayoutPipe,
        SchemaToFormGroupPipe
    ], ngImport: i0, template: "<header>\n\n  <h2>Existing Configs</h2>\n  <ng-container *ngIf='configLocalStorageService.hasConfigs()'>\n    <select [(ngModel)]='selectedKey'>\n      <option *ngFor='let configKey of configLocalStorageService.localAppConfigKeys'\n              [value]='configKey'>{{configKey}}\n      </option>\n    </select>\n    <button (click)='selectConfig()'>Select as default config</button>\n  </ng-container>\n\n  <button class=\"quit\" (click)='quit()'>Quit</button>\n\n</header>\n\n<main>\n\n  <form [formGroup]='readOnlyConfigForm'>\n\n    <ng-template ngFor [ngForOf]='readOnlyFormLayout' let-formLayoutItem>\n\n      <ng-template\n        [ngTemplateOutlet]='formItemDispatcher'\n        [ngTemplateOutletContext]='{\n          formLayoutItem: formLayoutItem,\n          form: readOnlyConfigForm,\n          readOnly: true\n          }'></ng-template>\n\n    </ng-template>\n\n    <ng-container *ngIf='configLocalStorageService.hasConfigs()'>\n      <button (click)='clearConfigs()'>Clear Configs</button>\n    </ng-container>\n\n  </form>\n\n  <h2>New Config Creation</h2>\n  <form [formGroup]='configForm' (ngSubmit)='onSubmit($event)'>\n\n    <ng-template ngFor [ngForOf]='formLayout' let-formLayoutItem>\n      <ng-template\n        [ngTemplateOutlet]='formItemDispatcher'\n        [ngTemplateOutletContext]='{\n          formLayoutItem: formLayoutItem,\n          form: configForm,\n          readOnly: false\n          }'></ng-template>\n    </ng-template>\n\n    <button type='submit'>Create new Config</button>\n\n  </form>\n\n</main>\n\n<footer>Angular Config Switcher | Made in Lune 2021</footer>\n\n\n<!-- TEMPLATES -->\n\n<ng-template #formItemDispatcher\n             let-readOnly='readOnly'\n             let-formLayoutItem='formLayoutItem'\n             let-form='form'>\n\n  <ng-container *ngIf=\"formLayoutItem.type === 'object'\">\n\n    <ng-template\n      [ngTemplateOutlet]='objectElement'\n      [ngTemplateOutletContext]='{\n          formLayoutItem: formLayoutItem,\n          form: form,\n          readOnly: readOnly\n          }'></ng-template>\n\n  </ng-container>\n\n  <ng-container *ngIf=\"formLayoutItem.type === 'string'\">\n\n    <ng-template\n      [ngTemplateOutlet]='inputElement'\n      [ngTemplateOutletContext]='{\n          control: formLayoutItem,\n          form: form,\n          readOnly: readOnly\n          }'>\n    </ng-template>\n\n  </ng-container>\n\n  <ng-container *ngIf=\"formLayoutItem.type === 'boolean'\">\n\n    <ng-template\n      [ngTemplateOutlet]='booleanElement'\n      [ngTemplateOutletContext]='{\n          control: formLayoutItem,\n          form: form,\n          readOnly: readOnly\n          }'>\n    </ng-template>\n\n  </ng-container>\n\n</ng-template>\n\n<ng-template #objectElement\n             let-readOnly='readOnly'\n             let-formLayoutItem='formLayoutItem'\n             let-form='form'>\n\n  <div class='form-group'>\n\n    <h3 [innerHTML]='formLayoutItem.name'></h3>\n\n    <div class='form-content-group'>\n\n      <ng-template ngFor [ngForOf]='formLayoutItem.controls' let-control>\n\n        <ng-template\n          [ngTemplateOutlet]='formItemDispatcher'\n          [ngTemplateOutletContext]='{\n          readOnly: readOnly,\n          formLayoutItem: control,\n          form: form.get(formLayoutItem.name)\n          }'></ng-template>\n\n      </ng-template>\n\n    </div>\n\n  </div>\n\n</ng-template>\n\n<ng-template #booleanElement let-control='control' let-form='form' let-readOnly='readOnly'>\n  <span [formGroup]='form' class='form-item'>\n    <label\n      [for]='control?.name'\n      [innerHTML]='control?.name'>\n    </label>\n    <input\n      type='checkbox'\n      [attr.name]='control?.name'\n      [formControlName]='control?.name'>\n  </span>\n</ng-template>\n\n<ng-template #inputElement let-control='control' let-form='form' let-readOnly='readOnly'>\n  <span [formGroup]='form' class='form-item'>\n    <label\n      [for]='control?.name'\n      [innerHTML]='control?.name'>\n    </label>\n    <input type='text'\n           [readonly]='readOnly'\n           [attr.id]='control?.name'\n           [placeholder]='control?.name'\n           [formControlName]='control?.name'>\n  </span>\n</ng-template>\n\n", styles: [":host{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif,\"Apple Color Emoji\",\"Segoe UI Emoji\";padding:1em;display:flex;flex-direction:column;background-color:var(--app-config-bg, lightsteelblue);width:100vw;max-width:800px;position:relative}:host button.quit{position:absolute;top:0;right:0}:host .form-group{padding:.5em 0}:host .form-group>h3{margin:0}:host .form-item{display:flex;align-items:center;padding:.5em 0}:host .form-item label{width:150px}:host input[type=text]{flex:1 1 auto}:host footer{text-align:right;font-size:11px}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i7.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "ngmodule", type: JsonToSchemaPipeModule }, { kind: "ngmodule", type: SchemaToFormLayoutPipeModule }, { kind: "ngmodule", type: SchemaToFormGroupPipeModule }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: SwitcherComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mil-config-switcher', encapsulation: ViewEncapsulation.Emulated, standalone: true, imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        FormsModule,
                        JsonToSchemaPipeModule,
                        SchemaToFormLayoutPipeModule,
                        SchemaToFormGroupPipeModule
                    ], providers: [
                        JsonToSchemaPipe,
                        SchemaToFormLayoutPipe,
                        SchemaToFormGroupPipe
                    ], template: "<header>\n\n  <h2>Existing Configs</h2>\n  <ng-container *ngIf='configLocalStorageService.hasConfigs()'>\n    <select [(ngModel)]='selectedKey'>\n      <option *ngFor='let configKey of configLocalStorageService.localAppConfigKeys'\n              [value]='configKey'>{{configKey}}\n      </option>\n    </select>\n    <button (click)='selectConfig()'>Select as default config</button>\n  </ng-container>\n\n  <button class=\"quit\" (click)='quit()'>Quit</button>\n\n</header>\n\n<main>\n\n  <form [formGroup]='readOnlyConfigForm'>\n\n    <ng-template ngFor [ngForOf]='readOnlyFormLayout' let-formLayoutItem>\n\n      <ng-template\n        [ngTemplateOutlet]='formItemDispatcher'\n        [ngTemplateOutletContext]='{\n          formLayoutItem: formLayoutItem,\n          form: readOnlyConfigForm,\n          readOnly: true\n          }'></ng-template>\n\n    </ng-template>\n\n    <ng-container *ngIf='configLocalStorageService.hasConfigs()'>\n      <button (click)='clearConfigs()'>Clear Configs</button>\n    </ng-container>\n\n  </form>\n\n  <h2>New Config Creation</h2>\n  <form [formGroup]='configForm' (ngSubmit)='onSubmit($event)'>\n\n    <ng-template ngFor [ngForOf]='formLayout' let-formLayoutItem>\n      <ng-template\n        [ngTemplateOutlet]='formItemDispatcher'\n        [ngTemplateOutletContext]='{\n          formLayoutItem: formLayoutItem,\n          form: configForm,\n          readOnly: false\n          }'></ng-template>\n    </ng-template>\n\n    <button type='submit'>Create new Config</button>\n\n  </form>\n\n</main>\n\n<footer>Angular Config Switcher | Made in Lune 2021</footer>\n\n\n<!-- TEMPLATES -->\n\n<ng-template #formItemDispatcher\n             let-readOnly='readOnly'\n             let-formLayoutItem='formLayoutItem'\n             let-form='form'>\n\n  <ng-container *ngIf=\"formLayoutItem.type === 'object'\">\n\n    <ng-template\n      [ngTemplateOutlet]='objectElement'\n      [ngTemplateOutletContext]='{\n          formLayoutItem: formLayoutItem,\n          form: form,\n          readOnly: readOnly\n          }'></ng-template>\n\n  </ng-container>\n\n  <ng-container *ngIf=\"formLayoutItem.type === 'string'\">\n\n    <ng-template\n      [ngTemplateOutlet]='inputElement'\n      [ngTemplateOutletContext]='{\n          control: formLayoutItem,\n          form: form,\n          readOnly: readOnly\n          }'>\n    </ng-template>\n\n  </ng-container>\n\n  <ng-container *ngIf=\"formLayoutItem.type === 'boolean'\">\n\n    <ng-template\n      [ngTemplateOutlet]='booleanElement'\n      [ngTemplateOutletContext]='{\n          control: formLayoutItem,\n          form: form,\n          readOnly: readOnly\n          }'>\n    </ng-template>\n\n  </ng-container>\n\n</ng-template>\n\n<ng-template #objectElement\n             let-readOnly='readOnly'\n             let-formLayoutItem='formLayoutItem'\n             let-form='form'>\n\n  <div class='form-group'>\n\n    <h3 [innerHTML]='formLayoutItem.name'></h3>\n\n    <div class='form-content-group'>\n\n      <ng-template ngFor [ngForOf]='formLayoutItem.controls' let-control>\n\n        <ng-template\n          [ngTemplateOutlet]='formItemDispatcher'\n          [ngTemplateOutletContext]='{\n          readOnly: readOnly,\n          formLayoutItem: control,\n          form: form.get(formLayoutItem.name)\n          }'></ng-template>\n\n      </ng-template>\n\n    </div>\n\n  </div>\n\n</ng-template>\n\n<ng-template #booleanElement let-control='control' let-form='form' let-readOnly='readOnly'>\n  <span [formGroup]='form' class='form-item'>\n    <label\n      [for]='control?.name'\n      [innerHTML]='control?.name'>\n    </label>\n    <input\n      type='checkbox'\n      [attr.name]='control?.name'\n      [formControlName]='control?.name'>\n  </span>\n</ng-template>\n\n<ng-template #inputElement let-control='control' let-form='form' let-readOnly='readOnly'>\n  <span [formGroup]='form' class='form-item'>\n    <label\n      [for]='control?.name'\n      [innerHTML]='control?.name'>\n    </label>\n    <input type='text'\n           [readonly]='readOnly'\n           [attr.id]='control?.name'\n           [placeholder]='control?.name'\n           [formControlName]='control?.name'>\n  </span>\n</ng-template>\n\n", styles: [":host{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif,\"Apple Color Emoji\",\"Segoe UI Emoji\";padding:1em;display:flex;flex-direction:column;background-color:var(--app-config-bg, lightsteelblue);width:100vw;max-width:800px;position:relative}:host button.quit{position:absolute;top:0;right:0}:host .form-group{padding:.5em 0}:host .form-group>h3{margin:0}:host .form-item{display:flex;align-items:center;padding:.5em 0}:host .form-item label{width:150px}:host input[type=text]{flex:1 1 auto}:host footer{text-align:right;font-size:11px}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_CONFIG]
                }] }, { type: i1.UntypedFormBuilder }, { type: AppConfigService }, { type: ConfigLocalStorageService }, { type: JsonToSchemaPipe }, { type: SchemaToFormLayoutPipe }, { type: SchemaToFormGroupPipe }]; } });

var switcher_component = /*#__PURE__*/Object.freeze({
    __proto__: null,
    defaultConfigKey: defaultConfigKey,
    SwitcherComponent: SwitcherComponent
});

const APP_CONFIG = new InjectionToken('app.config');
const getAppConfigProvider = (value) => ({
    provide: APP_CONFIG,
    deps: [ConfigLocalStorageService],
    useFactory: (configLocalStorageService) => {
        const hasLocalConfigSettings = configLocalStorageService.hasSettings();
        const defaultKey = configLocalStorageService.getDefaultKey();
        const isCustomConfig = defaultKey !== 'default';
        let appConfig;
        if (hasLocalConfigSettings && defaultKey) {
            configLocalStorageService.addConfig(defaultConfigKey, value);
            configLocalStorageService.read();
            appConfig = configLocalStorageService.getKey(defaultKey);
            appConfig.isDefault = !isCustomConfig;
        }
        else {
            appConfig = value;
        }
        return appConfig;
    }
});

/**
 * Generated bundle index. Do not edit.
 */

export { APP_CONFIG, AppConfigService, getAppConfigProvider };
